import { PARTNER_TEXT_TO_SEGMENT_MAX_LENGTH } from '@reach/shared-types/business-segments';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import posthog from 'posthog-js';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogBackdrop } from '@/components/ui/dialog-backdrop';
import { useFeatureSurface } from '../../FeatureSurfaceContext';
import { useAuth } from '../../../../hooks/useAuth';
import { textToPartnerSegment } from '../segment-definitions-api';
import styles from './DescribeSegmentModal.module.css';
import { clearAiSegmentDraft, isUsableAiSegmentDraft, type AiSegmentDraft } from './types';

const MODAL_TITLE = 'Describe your segment';
const MODAL_SUBTITLE = "Describe the audience in plain language and we'll build the conditions for you.";
const TEXTAREA_PLACEHOLDER = 'e.g., Contacts in California who placed an order in the last 30 days';
const LOADING_MESSAGE = 'Building your segment — this can take a few minutes.';

const VAGUE_ERROR_MESSAGE =
  'That description was too vague — add more detail, e.g. a field or a linked record.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong generating your segment. Please try again.';

const VAGUE_FAILURE_REASON = 'vague';
const API_ERROR_FAILURE_REASON = 'api_error';

const AI_SEGMENT_REQUESTED_EVENT = 'ai_segment_requested';
const AI_SEGMENT_SUCCEEDED_EVENT = 'ai_segment_succeeded';
const AI_SEGMENT_FAILED_EVENT = 'ai_segment_failed';

interface DescribeSegmentModalProps {
  onClose: () => void;
}

export const DescribeSegmentModal: React.FC<DescribeSegmentModalProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const featureSurface = useFeatureSurface();
  const { organization } = useAuth();
  const [text, setText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const goToBlankCreate = () => {
    // "Build manually" is an explicit request for a blank editor — drop any AI
    // draft mirror so a prior, abandoned generation can't re-seed the page.
    clearAiSegmentDraft();
    navigate(featureSurface.newSegmentPath);
    onClose();
  };

  const { mutate, isPending } = useMutation({
    mutationFn: textToPartnerSegment,
    onSuccess: data => {
      if (data.success && organization && isUsableAiSegmentDraft(data.conditionGroups)) {
        posthog.capture(AI_SEGMENT_SUCCEEDED_EVENT);
        const draft: AiSegmentDraft = {
          conditionGroups: data.conditionGroups,
          title: data.title,
          description: data.description,
          organizationId: organization.id,
        };
        navigate(featureSurface.newSegmentPath, { state: { aiSegmentDraft: draft } });
        onClose();
        return;
      }

      const failureReason = data.failureReason ?? API_ERROR_FAILURE_REASON;
      posthog.capture(AI_SEGMENT_FAILED_EVENT, { failureReason });
      const isVague = data.failureReason === VAGUE_FAILURE_REASON;
      setErrorMessage(isVague ? VAGUE_ERROR_MESSAGE : GENERIC_ERROR_MESSAGE);
    },
    onError: () => {
      posthog.capture(AI_SEGMENT_FAILED_EVENT, { failureReason: API_ERROR_FAILURE_REASON });
      setErrorMessage(GENERIC_ERROR_MESSAGE);
    },
  });

  const runGeneration = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    posthog.capture(AI_SEGMENT_REQUESTED_EVENT);
    setErrorMessage(null);
    mutate(trimmed);
  };

  const isBuildDisabled = !text.trim();
  const hasError = errorMessage !== null;

  const handleDismiss = () => {
    if (isPending) return;
    onClose();
  };

  return (
    <>
      <DialogBackdrop onClose={handleDismiss} />
      <dialog className={styles.dialog} open aria-busy={isPending}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>
              <Sparkles size={16} />
              {MODAL_TITLE}
            </h2>
            <p className={styles.subtitle}>{MODAL_SUBTITLE}</p>
          </div>
          <button
            className={styles.close}
            onClick={handleDismiss}
            disabled={isPending}
            aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {isPending ? (
          <section className={styles.loading} aria-live="polite">
            <span className={styles.spinner} aria-hidden />
            <p>{LOADING_MESSAGE}</p>
          </section>
        ) : hasError ? (
          <section className={styles.errorPanel} role="alert">
            <p className={styles.errorMessage}>{errorMessage}</p>
            <footer className={styles.recoveryActions}>
              <button className={styles.primaryAction} onClick={runGeneration}>
                Retry
              </button>
              <button className={styles.secondaryAction} onClick={() => setErrorMessage(null)}>
                Edit prompt
              </button>
              <button className={styles.secondaryAction} onClick={goToBlankCreate}>
                Build manually
              </button>
            </footer>
          </section>
        ) : (
          <section className={styles.form}>
            <label className={styles.fieldLabel} htmlFor="describe-segment-text">
              Segment description
            </label>
            <textarea
              id="describe-segment-text"
              className={styles.textarea}
              value={text}
              onChange={event => setText(event.target.value)}
              maxLength={PARTNER_TEXT_TO_SEGMENT_MAX_LENGTH}
              placeholder={TEXTAREA_PLACEHOLDER}
            />
            <footer className={styles.actions}>
              <button className={styles.secondaryAction} onClick={goToBlankCreate}>
                Build manually
              </button>
              <button
                className={styles.primaryAction}
                onClick={runGeneration}
                disabled={isBuildDisabled}>
                Build segment
              </button>
            </footer>
          </section>
        )}
      </dialog>
    </>
  );
};
